import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from '@store/slices/authSlice'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material'
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const Subscriptions = () => {
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const [subscriptions, setSubscriptions] = useState([])
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [paymentsLoading, setPaymentsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    if (token) {
      fetchSubscriptions()
      fetchPayments()
    } else {
      setError('Authentication token not found')
      setLoading(false)
    }
  }, [token])

  const fetchSubscriptions = async () => {
    try {
      setLoading(true)
      setError(null)

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/subscriptions?page=1&limit=50`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.')
        } else {
          throw new Error(`Server error: ${response.status}`)
        }
      }
      
      const result = await response.json()
      
      if (result.success) {
        setSubscriptions(result.subscriptions || [])
        setError(null)
      } else {
        throw new Error(result.message || 'Failed to fetch subscriptions')
      }
    } catch (error) {
      console.error('❌ Failed to fetch subscriptions:', error)
      setError(error.message || 'Failed to fetch subscriptions. Please try again.')
      setSubscriptions([])
    } finally {
      setLoading(false)
    }
  }

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true)

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/payments?page=1&limit=100`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.')
        } else {
          throw new Error(`Server error: ${response.status}`)
        }
      }
      
      const result = await response.json()
      
      if (result.success) {
        setPayments(result.payments || [])
      } else {
        throw new Error(result.message || 'Failed to fetch payments')
      }
    } catch (error) {
      console.error('❌ Failed to fetch payments:', error)
      setPayments([])
    } finally {
      setPaymentsLoading(false)
    }
  }

  const getDemoSubscriptions = () => [
    {
      id: 1,
      user: 'John Doe',
      email: 'john.doe@example.com',
      plan: 'pro',
      status: 'active',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2024-12-31T23:59:59Z',
      price: 29.99,
      currency: 'USD',
      provider: 'stripe',
      autoRenew: true,
      lastPayment: '2024-12-01T00:00:00Z'
    },
    {
      id: 2,
      user: 'Jane Smith',
      email: 'jane.smith@example.com',
      plan: 'basic',
      status: 'active',
      startDate: '2024-06-01T00:00:00Z',
      endDate: '2024-11-30T23:59:59Z',
      price: 9.99,
      currency: 'USD',
      provider: 'stripe',
      autoRenew: true,
      lastPayment: '2024-11-01T00:00:00Z'
    },
    {
      id: 3,
      user: 'Mike Johnson',
      email: 'mike.johnson@example.com',
      plan: 'free',
      status: 'active',
      startDate: '2024-03-01T00:00:00Z',
      endDate: null,
      price: 0,
      currency: 'USD',
      provider: 'manual',
      autoRenew: false,
      lastPayment: null
    },
    {
      id: 4,
      user: 'Sarah Wilson',
      email: 'sarah.wilson@example.com',
      plan: 'enterprise',
      status: 'active',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2024-12-31T23:59:59Z',
      price: 99.99,
      currency: 'USD',
      provider: 'stripe',
      autoRenew: true,
      lastPayment: '2024-12-01T00:00:00Z'
    },
    {
      id: 5,
      user: 'Ahmed Hassan',
      email: 'ahmed.hassan@example.com',
      plan: 'basic',
      status: 'expired',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2024-05-31T23:59:59Z',
      price: 9.99,
      currency: 'USD',
      provider: 'stripe',
      autoRenew: false,
      lastPayment: '2024-05-01T00:00:00Z'
    },
    {
      id: 6,
      user: 'Emily Davis',
      email: 'emily.davis@example.com',
      plan: 'pro',
      status: 'cancelled',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2024-08-31T23:59:59Z',
      price: 29.99,
      currency: 'USD',
      provider: 'stripe',
      autoRenew: false,
      lastPayment: '2024-08-01T00:00:00Z'
    }
  ]

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'error'
      case 'pro': return 'warning'
      case 'basic': return 'info'
      case 'free': return 'default'
      default: return 'default'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success'
      case 'expired': return 'error'
      case 'cancelled': return 'default'
      case 'pending': return 'warning'
      default: return 'default'
    }
  }

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'stripe': return '💳'
      case 'paypal': return '🅿️'
      case 'manual': return '✋'
      default: return '💳'
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success'
      case 'PENDING': return 'warning'
      case 'FAILED': return 'error'
      case 'CANCELLED': return 'default'
      default: return 'default'
    }
  }

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'PAYPAL': return '🅿️'
      case 'STRIPE': return '💳'
      case 'CASH': return '💵'
      default: return '💳'
    }
  }

  const handleViewSubscription = (subscription) => {
    setSelectedSubscription(subscription)
    setViewDialogOpen(true)
  }

  const handleDeleteSubscription = (subscription) => {
    setSubscriptionToDelete(subscription)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteSubscription = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/subscriptions/${subscriptionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        await fetchSubscriptions()
        setDeleteDialogOpen(false)
        setSubscriptionToDelete(null)
      } else {
        console.error('Failed to delete subscription')
      }
    } catch (error) {
      console.error('Error deleting subscription:', error)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('common.loadingSubscriptions')}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Payment History
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Manage subscriptions and view payment transactions
            </Typography>
          </Box>
        </Box>
      </motion.div>

      {/* Subscription Plans Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('plans.free')} {t('Plan')}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                      $0
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('subscriptions.remindersPerMonth')} 5
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'grey.300', color: 'text.primary', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                    F
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('plans.lite')} {t('Plan')}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                      $1.99
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('subscriptions.remindersPerMonth')} 20
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main', color: 'white', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                    L
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('plans.standard')} {t('Plan')}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                      $4.99
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('subscriptions.remindersPerMonth')} 50
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main', color: 'white', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                    S
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('plans.pro')} {t('Plan')}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                      $9.99
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t('subscriptions.remindersPerMonth')} 150
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'error.main', color: 'white', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                    P
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        <Card sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Subscriptions" />
              <Tab label="Payment History" />
            </Tabs>
          </Box>
        </Card>
      </motion.div>

      {/* Content based on active tab */}
      {activeTab === 0 && (
        <motion.div
          sx={{ mt: 4 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                {t('common.userSubscriptions')}
              </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{'User'}</TableCell>
                    <TableCell>{t('subscriptions.plan')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell>{t('subscriptions.startDate')}</TableCell>
                    <TableCell>{t('subscriptions.endDate')}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                            {subscription.user.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {subscription.user}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subscription.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`plans.${subscription.plan}`)}
                          size="small"
                          color={getPlanColor(subscription.plan)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`status.${subscription.status}`)}
                          size="small"
                          color={getStatusColor(subscription.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(subscription.startDate).format('MMM DD, YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {subscription.endDate
                            ? dayjs(subscription.endDate).format('MMM DD, YYYY')
                            : t('subscriptions.noEndDate')
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('common.view')}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewSubscription(subscription)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSubscription(subscription)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Payment History Table */}
      {activeTab === 1 && (
        <motion.div
          sx={{ mt: 4 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Payment Transactions
              </Typography>
              {paymentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading payments...</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Payer Email</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Transaction ID</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {payment.userId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                {payment.paymentData?.payer?.name?.given_name?.charAt(0) || payment.user?.phoneNumber?.charAt(0) || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {payment.user?.phoneNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                 {payment.userId}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {payment.paymentData?.payer?.email_address || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              ${payment.amount} {payment.currency}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.planType}
                              size="small"
                              color={getPlanColor(payment.planType.toLowerCase())}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status}
                              size="small"
                              color={getPaymentStatusColor(payment.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{getPaymentMethodIcon(payment.paymentMethod)}</span>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {payment.paymentMethod}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {payment.transactionId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {dayjs(payment.createdAt).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(payment.createdAt).format('hh:mm A')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {payments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No payment transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Subscription Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{'Subscription Details'}</DialogTitle>
        <DialogContent>
          {selectedSubscription && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {'User'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedSubscription.user}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {'Plan'}
                  </Typography>
                  <Chip
                    label={t(`plans.${selectedSubscription.plan}`)}
                    size="small"
                    color={getPlanColor(selectedSubscription.plan)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {'Status'}
                  </Typography>
                  <Chip
                    label={t(`status.${selectedSubscription.status}`)}
                    size="small"
                    color={getStatusColor(selectedSubscription.status)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('subscriptions.currency')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedSubscription.currency}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('subscriptions.startDate')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {dayjs(selectedSubscription.startDate).format('MMM DD, YYYY')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('subscriptions.endDate')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedSubscription.endDate
                      ? dayjs(selectedSubscription.endDate).format('MMM DD, YYYY')
                      : t('subscriptions.noEndDate')
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {'Last Payment'}
                  </Typography>
                  <Typography variant="body1">
                    {selectedSubscription.lastPayment
                      ? dayjs(selectedSubscription.lastPayment).format('MMM DD, YYYY hh:mm A')
                      : t('subscriptions.noPaymentsYet')
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('subscriptions.confirmDeleteMessage')} {subscriptionToDelete?.user}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={confirmDeleteSubscription} 
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Subscriptions
